import {
  AcquisitionChannel,
  Currency,
  isEnum,
  LoanStatusExternal,
  MetricAgentView,
  MetricAggregateView,
  ProductExternal,
  PurchaseCountry,
} from '@nemuru-eng/js-api-client';

import { TimeInterval } from '../../shared/entities/TimeInterval';
import { ScoreCardMetrics } from '../../shared/types/ScoreCardMetrics';

type Field = keyof Omit<MetricAggregateView, 'metrics'> | 'loanStatusExternal';
type Value = MetricAggregateView[keyof Omit<MetricAggregateView, 'metrics'>] | LoanStatusExternal;
export class MetricAnalyzer {
  constructor(
    public readonly timeInterval: TimeInterval,
    public readonly metricViews?: MetricAggregateView[] | MetricAgentView[],
  ) {}

  private field(value: Value): Field {
    if (isEnum(value, Currency)) return 'currency';
    if (isEnum(value, LoanStatusExternal)) return 'loanStatusExternal';
    if (isEnum(value, AcquisitionChannel)) return 'acquisitionChannel';
    if (isEnum(value, PurchaseCountry)) return 'purchaseCountry';
    if (isEnum(value, ProductExternal)) return 'productExternal';
  }

  public scoreCardMetrics(value: Value): ScoreCardMetrics {
    if (!this.metricViews)
      return {
        totalPrincipalAmount: {
          current: 0,
          previous: undefined,
          variation: undefined,
        },
        totalLoansNumber: {
          current: 0,
          previous: undefined,
          variation: undefined,
        },
      };

    let filteredMetricViews = this.metricViews;
    if (isEnum(value, LoanStatusExternal)) {
      filteredMetricViews = this.metricViews.map((metricView) => {
        return {
          ...metricView,
          metrics: metricView.metrics.filter((metric) => metric.loanStatusExternal === value),
        };
      });
    } else {
      const field = this.field(value);
      filteredMetricViews = this.metricViews.filter((metricView) => metricView[field] === value);
    }

    let currentIntervalTotalLoansNumber = 0;
    let currentIntervalTotalPrincipalAmount = 0;
    let previousIntervalTotalLoansNumber = 0;
    let previousIntervalTotalPrincipalAmount = 0;

    filteredMetricViews.forEach((metricView) => {
      metricView.metrics.forEach((metric) => {
        const currentIntervalMetrics = metric.metricsByDate.filter((metricByDate) =>
          this.timeInterval.isCurrent(metricByDate.date),
        );

        const previousIntervalMetrics = metric.metricsByDate.filter((metricByDate) =>
          this.timeInterval.isPrevious(metricByDate.date),
        );

        currentIntervalTotalLoansNumber += currentIntervalMetrics.reduce(
          (accumulator, { totalLoansNumber }) => accumulator + totalLoansNumber,
          0,
        );
        currentIntervalTotalPrincipalAmount += currentIntervalMetrics.reduce(
          (accumulator, { totalPrincipalAmount }) => accumulator + totalPrincipalAmount,
          0,
        );
        previousIntervalTotalLoansNumber += previousIntervalMetrics.reduce(
          (accumulator, { totalLoansNumber }) => accumulator + totalLoansNumber,
          0,
        );
        previousIntervalTotalPrincipalAmount += previousIntervalMetrics.reduce(
          (accumulator, { totalPrincipalAmount }) => accumulator + totalPrincipalAmount,
          0,
        );
      });
    });

    return {
      totalPrincipalAmount: {
        current: currentIntervalTotalPrincipalAmount,
        previous: previousIntervalTotalPrincipalAmount,
        variation:
          previousIntervalTotalPrincipalAmount === 0
            ? undefined
            : (currentIntervalTotalPrincipalAmount - previousIntervalTotalPrincipalAmount) /
              previousIntervalTotalPrincipalAmount,
      },
      totalLoansNumber: {
        current: currentIntervalTotalLoansNumber,
        previous: previousIntervalTotalLoansNumber,
        variation:
          previousIntervalTotalLoansNumber === 0
            ? undefined
            : (currentIntervalTotalLoansNumber - previousIntervalTotalLoansNumber) / previousIntervalTotalLoansNumber,
      },
    };
  }
}
